// Replaced: import { PushNotifications } from "@capacitor/push-notifications";
// ASSUMED: You have installed and imported the new Firebase Messaging Plugin
import { FirebaseMessaging } from "@capacitor-firebase/messaging";
import { Preferences } from "@capacitor/preferences";
import { Device } from "@capacitor/device";
import { App } from "@capacitor/app";
import { registerDevice } from "../services/api/device";
import { isNativePlatform } from "../utils/platformUtil";

const STORAGE_KEY = "lastDeviceRegistration";

/**
 * Checks if the device registration API call should be executed today.
 */
async function shouldRegister(): Promise<boolean> {
    const { value } = await Preferences.get({ key: STORAGE_KEY });
    if (!value) return true;
    const lastDate = new Date(value);
    const now = new Date();
    return (
        lastDate.getFullYear() !== now.getFullYear() ||
        lastDate.getMonth() !== now.getMonth() ||
        lastDate.getDate() !== now.getDate()
    );
}
let listenersRegistered = false;

/**
 * Registers the device for push notifications (FCM Token) and sends device info to the API.
 */
export async function registerPushDevice() {
    if (!isNativePlatform()) return;

    if (!listenersRegistered) {
        listenersRegistered = true;

        // CRITICAL CHANGE: Listen for the FirebaseMessaging 'tokenReceived' event
        FirebaseMessaging.addListener("tokenReceived", async (event) => {
            const tokenValue = event.token;

            console.log("FCM Token Received:", tokenValue);
            // Optional: toast.info(`FCM Token: ${tokenValue}`, {autoClose: false} ); 

            if (!(await shouldRegister())) {
                // toast.info("Already registered today, skipping API call", {autoClose: 2000});
                console.log("Already registered today, skipping API call");
                return;
            }

            const info = await Device.getInfo();
            const id = await Device.getId();
            const appInfo = await App.getInfo();

            try {
                const res = await registerDevice({
                    device_id: id.identifier,
                    device_model: info.model ?? "unknown",
                    platform: info.platform,
                    os_version: info.osVersion ?? "unknown",
                    app_version: appInfo.version ?? "unknown",
                    device_type: window.innerWidth >= 768 ? "tablet" : "mobile",
                    push_token: tokenValue, // Use the received FCM token
                });

                console.log("Device registered", JSON.stringify(res));
                // toast.success("Device registered successfully", {autoClose: 2000});

                await Preferences.set({
                    key: STORAGE_KEY,
                    value: new Date().toISOString(),
                });
            } catch (error) {
                console.error("Device registration failed", error);
                // Updated to toast.error for actual failures
                // toast.error("Device API registration failed: "+ JSON.stringify(error), {autoClose: false});
            }
        });
        
        // Removed FirebaseMessaging.addListener("tokenError", ...) to resolve compilation error.
    }
    
    // Request permission (FCM plugin handles the native request)
    // Note: Some FCM plugins automatically request permission on getToken/addListener.
    const permission = await FirebaseMessaging.requestPermissions();
    if (permission.receive !== "granted") {
        console.warn("Push permission not granted");
        return;
    }

    // CRITICAL CHANGE: Manually request the token to trigger the 'tokenReceived' listener
    try {
        await FirebaseMessaging.getToken();
    } catch (e) {
        console.error("Failed to call FirebaseMessaging.getToken()", e);
        // toast.error("Failed to initiate FCM token request", {autoClose: false});
    }
}